import qrcode from 'qrcode-generator';

/** QR code with a logo in the middle, drawn on a canvas.
 *  Error correction H (~30% of modules recoverable) — the logo covers ~20% of the
 *  area, so the code still scans with a cheap phone camera. */

export interface QrOptions {
  size?: number;            // output px
  background: 'white' | 'transparent';
  logoSrc?: string | null;  // same-origin path or a CORS-enabled URL
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

export async function drawQr(canvas: HTMLCanvasElement, text: string, opts: QrOptions) {
  const size = opts.size ?? 1024;
  const qr = qrcode(0, 'H');
  qr.addData(text);
  qr.make();
  const n = qr.getModuleCount();
  const quiet = 4;
  const cell = size / (n + quiet * 2);

  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  ctx.clearRect(0, 0, size, size);
  if (opts.background === 'white') {
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, size, size);
  }

  // Keep the logo box clear of modules instead of painting over them: the
  // decoder then sees clean «missing» modules, which is what error correction
  // recovers best.
  const logoModules = Math.floor(n * 0.24) | 1;
  const logoStart = Math.floor((n - logoModules) / 2);
  const inLogo = (r: number, c: number) =>
    !!opts.logoSrc && r >= logoStart && r < logoStart + logoModules && c >= logoStart && c < logoStart + logoModules;

  ctx.fillStyle = '#000000';
  for (let r = 0; r < n; r++) {
    for (let c = 0; c < n; c++) {
      if (!qr.isDark(r, c) || inLogo(r, c)) continue;
      // +0.5 px overlap removes hairline seams between modules when printed.
      ctx.fillRect((c + quiet) * cell, (r + quiet) * cell, cell + 0.5, cell + 0.5);
    }
  }

  if (opts.logoSrc) {
    const box = logoModules * cell;
    const x = (logoStart + quiet) * cell;
    const y = x;
    // White pad even on a transparent export — the logo must never sit on the paper colour.
    const radius = box * 0.18;
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.arcTo(x + box, y, x + box, y + box, radius);
    ctx.arcTo(x + box, y + box, x, y + box, radius);
    ctx.arcTo(x, y + box, x, y, radius);
    ctx.arcTo(x, y, x + box, y, radius);
    ctx.closePath();
    ctx.fill();
    try {
      const img = await loadImage(opts.logoSrc);
      // Логотип у нас растровый и небольшой (surf-coffee.png — 270×270), а
      // коробка под него в экспорте 2048 px заметно больше: по умолчанию canvas
      // тянет такую картинку дешёвым фильтром, и лого выходит мылом (Алексей
      // 18.09, AI57). Просим качественную интерполяцию.
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      const pad = box * 0.1;
      const avail = box - pad * 2;
      const scale = Math.min(avail / img.width, avail / img.height);
      const w = img.width * scale;
      const h = img.height * scale;
      ctx.drawImage(img, x + (box - w) / 2, y + (box - h) / 2, w, h);
    } catch {
      // Logo failed to load: the white pad stays, the code still scans.
    }
  }
}

export async function qrPngBlob(text: string, opts: QrOptions): Promise<Blob> {
  const canvas = document.createElement('canvas');
  await drawQr(canvas, text, { ...opts, size: opts.size ?? 2048 });
  return new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob failed'))), 'image/png'),
  );
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}
